/**
 * Crisp HTML overlay labels for theater entities.
 * Canvas bitmap fonts look chunky when the low-res buffer is upscaled;
 * regular monospace in DOM stays readable.
 *
 * Labels open an in-page detail drawer (via onActivate) — they do not
 * navigate away. Hit area is enlarged for mobile.
 */

export type OverlayLabel = {
  id: string;
  text: string;
  /** Buffer-space anchor (center-x, top-of-label). */
  x: number;
  y: number;
  /** Accessible name (includes “opens details”). */
  ariaLabel: string;
  /** Optional 0–2 buffer-pixel nudge under the feet (not a large stagger). */
  lane?: number;
};

export class LabelOverlay {
  private root: HTMLElement;
  private nodes = new Map<string, HTMLButtonElement>();
  private bufferW = 1;
  private bufferH = 1;
  private onActivate: ((id: string) => void) | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
  }

  setActivateHandler(fn: ((id: string) => void) | null): void {
    this.onActivate = fn;
  }

  setBufferSize(w: number, h: number): void {
    this.bufferW = Math.max(1, w);
    this.bufferH = Math.max(1, h);
  }

  /** Sync overlay to the current frame's labels (creates/updates/removes). */
  sync(labels: OverlayLabel[]): void {
    const seen = new Set<string>();
    for (const lab of labels) {
      seen.add(lab.id);
      let btn = this.nodes.get(lab.id);
      if (!btn) {
        btn = document.createElement("button");
        btn.type = "button";
        btn.className = "entity-label";
        btn.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn!.dataset.labelId;
          if (id) this.onActivate?.(id);
        });
        this.root.appendChild(btn);
        this.nodes.set(lab.id, btn);
      }
      btn.dataset.labelId = lab.id;
      let chip = btn.querySelector<HTMLSpanElement>(".entity-label-chip");
      if (!chip) {
        chip = document.createElement("span");
        chip.className = "entity-label-chip";
        btn.replaceChildren(chip);
      }
      if (chip.textContent !== lab.text) chip.textContent = lab.text;
      if (btn.getAttribute("aria-label") !== lab.ariaLabel) {
        btn.setAttribute("aria-label", lab.ariaLabel);
      }

      // lane is a tiny buffer-pixel nudge (0–2), not a large stagger strip.
      const lane = Math.min(2, Math.max(0, lab.lane ?? 0));
      const px = (lab.x / this.bufferW) * 100;
      const py = ((lab.y + lane) / this.bufferH) * 100;
      btn.style.left = `${px}%`;
      btn.style.top = `${py}%`;
    }

    for (const [id, node] of this.nodes) {
      if (seen.has(id)) continue;
      node.remove();
      this.nodes.delete(id);
    }
  }

  clear(): void {
    for (const node of this.nodes.values()) node.remove();
    this.nodes.clear();
  }
}
